// Auto-recálculo de "Revisa tu crédito" (revisión UI 2026-08-26, iterada con
// feedback: bandera + código de moneda, slider continuo como inversiones, y
// CERO interrupciones al escribir — el patrón del simulador de transfers).
//
// El card vive DENTRO del #vue-app global: Vue compila ese HTML como template
// (un <script> inline ahí muere) y REEMPLAZA los nodos al montar. Por eso:
// - los listeners simples van DELEGADOS en `document`;
// - IMask (que necesita instancia sobre el nodo) se attachea vía
//   MutationObserver cuando el input definitivo aparece.
//
// Regla fintech intacta: acá JAMÁS se calcula ni formatea dinero. IMask solo
// enmascara el NÚMERO que el usuario tipea (con el iso code que el servidor
// puso en data-currency); cada cambio pide la cotización al servidor, que
// devuelve montos formateados Y el contrato + su SHA-256 frescos (decisión 11).

import IMask from 'imask';
import { isAccessDenied, readResponse, messageFor, announce } from './card-access-response';
import { renderCreditMoney, formatCreditMoneyContainer } from './credit-money-text';

let mask = null;
let debounce = null;
// La página ya incluye una cotización y un contrato válidos del servidor.
// Desde la primera edición solo una respuesta vigente puede habilitarlos.
let cotizacionVigente = true;

const $ = (id) => document.getElementById(id);
const slider = () => $('credit-term-slider');

// --- Slider continuo (calcado de investment-new-form): la perilla se
// desliza libre (0..N*100) y hace snap al plazo al soltar. ---
const STEP_SCALE = 100;

function terms() {
  return JSON.parse(slider().dataset.termOptions || '[]');
}
function termIndex() {
  return Math.max(0, Math.min(Math.round(parseInt(slider().value, 10) / STEP_SCALE), terms().length - 1));
}
function currentOption() {
  return terms()[termIndex()] || {
    term: Number($('credit-form-term').value),
    term_unit: $('credit-form-term-unit').value,
    label: $('credit-term-label').textContent
  };
}
function termLabelText() {
  return currentOption().label;
}

function amountCents() {
  if (!mask) return 0;
  const unidades = parseInt(mask.unmaskedValue || '0', 10);
  return unidades * parseInt($('credit-amount-input').dataset.subunit, 10);
}

// Recorrido morado hasta la perilla (como el slider de inversiones). Se
// pinta en los tres momentos: al montar, al arrastrar y al re-renderizar.
function pintarSlider() {
  const s = slider();
  if (!s) return;
  const max = parseFloat(s.max) || 1;
  s.style.setProperty('--credits-slider-pct', `${(parseFloat(s.value) / max) * 100}%`);
  // Un lector de pantalla anuncia el value interno (0..N*100): "300" no
  // significa nada. aria-valuetext dice lo que la etiqueta visual dice.
  if ($('credit-term-label')) s.setAttribute('aria-valuetext', termLabelText());
}

function renderTerms(nuevos, mantener) {
  const s = slider();
  s.dataset.termOptions = JSON.stringify(nuevos);
  s.max = String((nuevos.length - 1) * STEP_SCALE);
  const idx = nuevos.findIndex(option => option.term === mantener.term && option.term_unit === mantener.term_unit);
  s.value = String((idx >= 0 ? idx : 0) * STEP_SCALE);
  const cont = $('credit-term-steps');
  cont.innerHTML = '';
  nuevos.forEach(() => {
    const marca = document.createElement('span');
    marca.className = 'credits-slider-step';
    cont.appendChild(marca);
  });
  $('credit-term-label').textContent = termLabelText();
  pintarSlider();
}

function aplicar(data, centsPedidos, termPedido, unidadPedida) {
  const valido = data.valid === true;
  const options = data.term_options || [];
  const served = options.find(option => option.term === data.served_term && option.term_unit === data.served_term_unit);
  const requested = options.find(option => option.term === termPedido && option.term_unit === unidadPedida);
  // El servidor puede elegir otro plazo válido si el nuevo monto lo exige.
  // Solo se instala una cotización de ese monto y de una opción publicada.
  if (valido && (!served || data.served_amount_cents !== centsPedidos ||
      (requested && served.key !== requested.key))) {
    throw new Error('Quote does not match an available term and the requested amount');
  }
  const warning = $('credit-limit-warning');
  if (options.length) {
    const previous = currentOption();
    renderTerms(options, valido ? served : previous);
    if (!valido && !options.some(option => option.term === previous.term && option.term_unit === previous.term_unit)) {
      // Hay otro plazo disponible para este monto. La respuesta definitiva
      // llegará en la recotización siguiente; todavía no es un error de monto.
      renderCreditMoney(warning, warning.dataset.updating || '');
      recotizar();
      return;
    }
  }
  // Los límites y errores reales conservan el mensaje preparado por el servidor.
  renderCreditMoney(warning, valido ? '' : (data.limit_message || ''));
  if (!valido) return;

  ['installment', 'periodic_total', 'periodic_admin_fee',
    'admin_fee_label', 'periodic_total_label', 'incorporation_fee']
    .forEach((campo) => {
      const el = document.querySelector(`[data-quote="${campo}"]`);
      if (el && data[campo] != null) renderCreditMoney(el, data[campo]);
    });
  $('credit-incorporation-row').hidden = data.incorporation_fee_positive !== true;

  const tbody = $('credit-schedule-body');
  tbody.innerHTML = '';
  (data.schedule || []).forEach((fila) => {
    const tr = document.createElement('tr');
    [fila.number, fila.due_on, fila.principal, fila.interest, fila.admin_fee].forEach((celda) => {
      const td = document.createElement('td');
      renderCreditMoney(td, celda);
      tr.appendChild(td);
    });
    const tdTotal = document.createElement('td'); // el total viene YA formateado del servidor
    const b = document.createElement('b');
    renderCreditMoney(b, fila.total);
    tdTotal.appendChild(b);
    tr.appendChild(tdTotal);
    tbody.appendChild(tr);
  });

  // Contrato + SHA frescos DEL SERVIDOR: lo exhibido y lo firmado, juntos.
  $('credit-contract-box').innerHTML = data.contract_html;
  $('credit-form-sha').value = data.exhibited_sha256;
  $('credit-form-version').value = data.contract_version;
  // La terna firmada sale de la MISMA respuesta que trajo el sha exhibido: si
  // el servidor recuperó el plazo (porque el pedido ya no aplicaba), se firma
  // el que efectivamente cotizó, no el que mandó el navegador.
  $('credit-form-amount').value = data.served_amount_cents ?? centsPedidos;
  $('credit-form-term').value = data.served_term ?? termPedido;
  $('credit-form-term-unit').value = data.served_term_unit;
  // Aceptar el contrato anterior nunca acepta este: primero se reemplaza
  // todo lo exhibido y firmado, luego se permite una aceptación nueva.
  $('credit-accept-checkbox').checked = false;
  cotizacionVigente = true;
  actualizarConsentimiento();
}

// Recotiza con LO TIPEADO, sin tocar jamás el input (patrón del simulador):
// si la combinación no vale, el aviso lo dice y el submit se bloquea — el
// usuario corrige a su ritmo. El servidor re-valida en el create igual.
let seq = 0;

// La misma consulta para recotizar y para cambiar de moneda. Resuelve con el
// JSON o con una marca de rechazo (__throttled, __denied); rechaza ante una
// falla de red o el timeout.
function pedirCotizacion(query) {
  const url = `${$('credit-amount-input').dataset.quoteUrl}?${query}`;
  // Timeout: sin esto una petición colgada deja el botón bloqueado para siempre.
  const corte = new AbortController();
  const reloj = setTimeout(() => corte.abort(), 8000);
  return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin',
                      signal: corte.signal })
    .then(async (r) => {
      clearTimeout(reloj);
      if (r.status === 429) return { __throttled: true };
      // 401 (sesión vencida) y 403 (sin acceso) tampoco traen `valid`: el
      // mismo botón muerto y mudo.
      if (isAccessDenied(r)) return { __denied: r, body: await readResponse(r) };
      return r.json();
    })
    .catch((error) => {
      clearTimeout(reloj);
      throw error;
    });
}

// Dice por qué no hubo cotización cuando el servidor la rechazó. Devuelve
// false si la respuesta trae una cotización (válida o no).
function avisarRechazo(data) {
  const warning = $('credit-limit-warning');
  // 429 del throttle (30/min por IP — compartida en oficinas/CGNAT): el
  // body es {error:...} sin `valid`, que antes caía en aplicar() como
  // "inválido sin mensaje" — botón muerto y MUDO. Decirlo, como la red.
  if (data.__throttled) {
    renderCreditMoney(warning, warning.dataset.throttled);
    return true;
  }
  // Decirlo y, si el servidor lo pide (kyc_required), llevar al paso del
  // asistente KYC que corresponde. La cotización ya no lo pide (el asistente
  // se exige al enviar la solicitud), pero el camino queda por si vuelve.
  if (data.__denied) {
    const messages = { accessDenied: warning.dataset.accessDenied,
                       sessionRequired: warning.dataset.sessionRequired };
    renderCreditMoney(warning, messageFor(data.__denied, data.body, messages));
    announce(data.__denied, data.body, messages);
    return true;
  }
  return false;
}

function recotizar() {
  clearTimeout(debounce);
  debounce = null;
  const cents = amountCents();
  const option = currentOption();
  const term = option.term;
  const termUnit = option.term_unit;
  invalidarFirma(); // por si se llega acá sin pasar por el debounce (slider)
  const ticket = seq;
  pedirCotizacion(`amount=${cents}&term=${term}&term_unit=${encodeURIComponent(termUnit)}&currency=${encodeURIComponent($('credit-amount-input').dataset.currency)}`)
    .then((data) => {
      // Respuesta fuera de orden: una petición lenta jamás pisa a una más
      // nueva (exhibiría dinero que no corresponde a lo tipeado).
      if (ticket !== seq || avisarRechazo(data)) return;
      aplicar(data, cents, term, termUnit);
    })
    .catch(() => {
      if (ticket !== seq) return;
      // Fallo de red: decirlo y dejar salida, jamás un botón muerto y mudo.
      renderCreditMoney($('credit-limit-warning'), $('credit-limit-warning').dataset.networkError);
    });
}

// --- Cambio de moneda sin recargar (selector del campo de monto) ---
// La otra moneda se cotiza con el mismo endpoint, sin monto: el servidor
// parte del máximo, como al abrir la página. Mientras llega, el monto y el
// plazo no se editan; recién una cotización válida reemplaza límites,
// máscara, plazos, resumen y contrato. Si no llega, el selector vuelve a la
// moneda de la página y la cotización anterior, intacta, sigue vigente.
function urlDeMoneda(moneda) {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('currency', moneda);
  return url;
}

function bloquearMontoYPlazo(bloqueado) {
  $('credit-amount-input').readOnly = bloqueado;
  slider().disabled = bloqueado;
  document.querySelectorAll('.credits-amount-field, .credits-slider-container')
    .forEach((el) => el.classList.toggle('credits-stale', bloqueado));
}

function instalarMoneda(data) {
  const input = $('credit-amount-input');
  Object.assign(input.dataset, {
    currency: data.currency, subunit: String(data.subunit), min: String(data.min_amount_cents),
    max: String(data.max_amount_cents), step: String(data.step_cents), moneyInputCurrency: data.currency
  });
  // La máscara lleva el código de la moneda: se rehace sobre el monto servido.
  if (mask) { try { mask.destroy(); } catch (_e) { /* nodo ya huérfano */ } }
  mask = null;
  input.value = String(data.served_amount_units);
  attachMask();
  input.dispatchEvent(new Event('money-input:refresh'));
  $('credit-form-currency').value = data.currency;
  $('credit-collection-order').textContent = data.collection_order;
  const volver = document.querySelector('[data-credits-back-link]');
  if (volver) {
    const destino = new URL(volver.href);
    destino.searchParams.set('currency', data.currency);
    volver.href = destino;
  }
  // Recargar abre la moneda elegida, sin el monto, el plazo ni el ancla del
  // contrato de la anterior.
  window.history.replaceState(window.history.state, '', urlDeMoneda(data.currency));
}

function cambiarMoneda(moneda, revertir) {
  const vigente = $('credit-amount-input').dataset.currency;
  // Solo una cotización que valía ANTES del cambio puede volver a valer. Si
  // el usuario ya había editado (debounce pendiente, respuesta en vuelo o una
  // recotización fallida), lo firmado no es lo que muestra el campo.
  const valiaAntes = cotizacionVigente;
  // Una recotización pendiente de la moneda anterior ya no corresponde.
  clearTimeout(debounce);
  debounce = null;
  invalidarFirma();
  const ticket = seq;
  // El selector vuelve a la moneda que la página muestra de verdad y, si
  // nada cambió, la cotización anterior sigue valiendo.
  const quedarse = (restaurar) => {
    bloquearMontoYPlazo(false);
    const actual = $('credit-amount-input').dataset.currency;
    revertir(actual);
    if (restaurar && valiaAntes && actual === vigente) restaurarCotizacion();
  };
  bloquearMontoYPlazo(true);
  renderCreditMoney($('credit-limit-warning'), $('credit-limit-warning').dataset.updating || '');
  pedirCotizacion(`currency=${encodeURIComponent(moneda)}`)
    .then((data) => {
      if (ticket !== seq) return;
      if (avisarRechazo(data)) {
        quedarse(Boolean(data.__throttled));
        return;
      }
      const servida = (data.term_options || [])
        .some(option => option.term === data.served_term && option.term_unit === data.served_term_unit);
      if (data.valid !== true || data.currency !== moneda || !servida) {
        // Sin oferta que mostrar en esa moneda: la página del servidor dice
        // por qué, igual que antes del cambio sin recarga.
        window.location.assign(urlDeMoneda(moneda));
        return;
      }
      bloquearMontoYPlazo(false);
      instalarMoneda(data);
      aplicar(data, data.served_amount_cents, data.served_term, data.served_term_unit);
    })
    .catch(() => {
      if (ticket !== seq) return;
      renderCreditMoney($('credit-limit-warning'), $('credit-limit-warning').dataset.networkError);
      quedarse(true);
    });
}

// Lo exhibido y lo firmado no se tocaron: la cotización anterior vale.
function restaurarCotizacion() {
  cotizacionVigente = true;
  actualizarConsentimiento();
}

document.addEventListener('currency-select:change', (event) => {
  const { currency, revert } = event.detail || {};
  if (!currency || !$('credit-amount-input')) return;
  event.preventDefault();
  cambiarMoneda(currency, revert);
});
// Invalida la firma EN EL INSTANTE del cambio (no al final del debounce): en
// esa ventana el formulario aún lleva la terna vieja y COHERENTE (monto+plazo
// +sha), así que el amarre del create no la rechazaría — se podía firmar un
// monto distinto al exhibido. Bloquear no interrumpe el tipeo: no toca ni el
// input ni el caret.
function invalidarFirma() {
  // Invalida también cualquier respuesta en vuelo desde el instante de la
  // edición, antes de que los 350 ms del debounce envíen la consulta nueva.
  seq += 1;
  cotizacionVigente = false;
  $('credit-accept-checkbox').checked = false;
  actualizarConsentimiento();
}

function actualizarConsentimiento() {
  const aceptacion = $('credit-accept-checkbox');
  aceptacion.disabled = !cotizacionVigente;
  if (!cotizacionVigente) aceptacion.checked = false;
  $('credit-submit').disabled = !cotizacionVigente || !aceptacion.checked;
  // Las cifras anteriores siguen visibles, pero no se presentan como una
  // cotización vigente durante la edición, la consulta o un error.
  $('credit-quote-summary').classList.toggle('credits-stale', !cotizacionVigente);
}

function recotizarDebounced() {
  invalidarFirma();
  clearTimeout(debounce);
  debounce = setTimeout(recotizar, 350);
}

// IMask sobre el nodo DEFINITIVO (post-mount de Vue): "num CLP" — miles con
// punto y el iso code siempre visible, sin pelear con el caret del usuario.
function attachMask() {
  const input = $('credit-amount-input');
  if (!input) return;
  // Identidad por NODO, jamás por un data-attr: una marca en el DOM crudo
  // contamina el template que Vue captura al montar, y el nodo remontado
  // nacería "marcado" sin máscara real.
  if (mask && mask.el && mask.el.input === input) return;
  ['credit-quote-summary', 'credit-schedule-body', 'credit-limit-warning']
    .forEach(id => formatCreditMoneyContainer($(id)));
  if (mask) { try { mask.destroy(); } catch (_e) { /* nodo ya huérfano */ } }
  mask = IMask(input, {
    mask: `num ${input.dataset.currency}`,
    lazy: false,
    blocks: {
      num: { mask: Number, signed: false, thousandsSeparator: '.', radix: ',', scale: 0 },
    },
  });
  mask.on('accept', recotizarDebounced);
  pintarSlider();
  actualizarConsentimiento();
  window.__creditsMask = mask; // para specs de sistema (fill programático no atraviesa IMask)
}

document.addEventListener('DOMContentLoaded', () => {
  attachMask();
  // El observer queda SIEMPRE activo: Vue reemplaza el nodo al montar (después
  // de DOMContentLoaded) y la máscara del nodo original queda huérfana — cada
  // vez que aparece un input sin marca, se re-attachea al nodo definitivo.
  const observer = new MutationObserver(() => attachMask());
  observer.observe(document.body, { childList: true, subtree: true });
});

// Delegación para las ediciones (sobrevive al re-render de Vue). El evento
// nativo también invalida cuando IMask rechaza lo tipeado sin emitir accept.
document.addEventListener('input', (e) => {
  if (e.target.id === 'credit-amount-input') recotizarDebounced();
  if (e.target.id === 'credit-term-slider') {
    // Volver al valor inicial durante el arrastre puede no emitir change.
    // El debounce recupera la cotización también al terminar ese gesto.
    recotizarDebounced();
    $('credit-term-label').textContent = termLabelText();
    pintarSlider();
  }
});

document.addEventListener('change', (e) => {
  if (e.target.id === 'credit-accept-checkbox') actualizarConsentimiento();
});

// Un checkbox disabled no participa en required. Enter o requestSubmit()
// deben respetar la misma condición que el botón, también tras montar Vue.
document.addEventListener('submit', (e) => {
  if (e.target.id !== 'credit-request-form') return;
  const aceptacion = $('credit-accept-checkbox');
  if (cotizacionVigente && !aceptacion.disabled && aceptacion.checked) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  actualizarConsentimiento();
}, true);

// --- Accesibilidad de los modales CSS-only (patrón del FAQ de referrals) ---

// ESC cierra el modal abierto: sin esto la única salida era el clic.
// dispatchEvent: marcar checked por código NO dispara `change`, y el manejo
// de foco de abajo vive ahí.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  document.querySelectorAll('.credits-modal-toggle:checked').forEach((t) => {
    t.checked = false;
    t.dispatchEvent(new Event('change', { bubbles: true }));
  });
});

// Foco de los modales CSS-only: al abrir, el foco viaja al diálogo (sin esto
// el teclado y el lector de pantalla se quedan detrás de la máscara); al
// cerrar, vuelve a donde estaba.
// De dónde vino el usuario, capturado en el CLICK y no en el `change`: al
// pinchar un <label for=toggle> el navegador mueve el foco al checkbox ANTES
// de despachar change, así que document.activeElement era el propio toggle
// —oculto, aria-hidden y al final del documento— y "restaurar" ahí dejaba el
// foco en la nada. Se ignora el aspa de cierre (que también es un trigger) y
// se aceptan botones y enlaces, porque el CTA "Tomar crédito" del carrusel
// abre su modal por código.
let focoPrevio = null;
document.addEventListener('click', (e) => {
  if (!e.target.closest) return;
  if (e.target.closest('.credits-modal__close, [data-credits-modal-dismiss]')) return;

  const origen = e.target.closest('[data-credits-modal-trigger], button, a');
  if (origen) focoPrevio = origen;
}, true);

document.addEventListener('change', (e) => {
  if (!e.target.classList || !e.target.classList.contains('credits-modal-toggle')) return;
  // Estructura fija de los modales: toggle → label máscara → div[role=dialog].
  const dialogo = e.target.nextElementSibling && e.target.nextElementSibling.nextElementSibling;
  if (!dialogo || dialogo.getAttribute('role') !== 'dialog') return;
  if (e.target.checked) {
    dialogo.setAttribute('tabindex', '-1');
    dialogo.focus();
  } else if (focoPrevio && document.contains(focoPrevio)) {
    focoPrevio.focus();
    focoPrevio = null;
  }
});

// Un <label> no se activa con teclado por sí solo: Enter y Espacio deben
// abrir/cerrar igual que el clic.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  const trigger = e.target.closest('[data-credits-modal-trigger]');
  if (!trigger) return;
  e.preventDefault();
  trigger.click();
});
document.addEventListener('change', (e) => {
  if (e.target.id === 'credit-term-slider') {
    // Snap de la perilla al plazo elegido, como el slider de inversiones.
    e.target.value = String(termIndex() * STEP_SCALE);
    recotizar();
  }
});
